// src/pages/MobileCustomerFlowPage.js

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import CustomerFlow from '../components/CustomerFlow/CustomerFlow';
import { trackMobileCustomerFlowStarted } from '../analytics/events';

const STEP_STATUSES_KEY = 'temp_stepStatuses';
const ORIGINAL_TOKEN_KEY = 'temp_originalToken';

const MobileCustomerFlowPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowData, setFlowData] = useState(null);
  const [stepStatuses, setStepStatuses] = useState({});

  // Guarda el token original en localStorage para que el bridge page pueda redirigir al retorno de Valida
  useEffect(() => {
    if (token) {
      localStorage.setItem(ORIGINAL_TOKEN_KEY, token);
    }
  }, [token, searchParams]);

  const initializeFlow = useCallback(() => {
    try {
      const tokenScope = JSON.parse(localStorage.getItem('temp_tokenScope') || '{}');
      const flowConfig = tokenScope.flow_config;
      const requestUid = tokenScope.request_uid;

      const tokenCustomerId =
        localStorage.getItem('temp_tokenCustomerUid') || localStorage.getItem('temp_tokenCustomerId');
      const tokenRequestId = localStorage.getItem('temp_tokenRequestId');

      if (!tokenCustomerId || !tokenRequestId) {
        throw new Error('Este enlace ya no está disponible. Pedile a tu vendedor un nuevo enlace para continuar');
      }

      if (!flowConfig || !Array.isArray(flowConfig) || flowConfig.length === 0) {
        throw new Error('La configuración del flujo no está disponible. Pedile a tu vendedor un nuevo enlace para continuar');
      }

      // Los estados se persisten atados al token para sobrevivir el retorno de Valida.
      // Un token distinto (QR nuevo) siempre arranca limpio.
      const savedEntry = JSON.parse(localStorage.getItem(STEP_STATUSES_KEY) || '{}');
      const savedStatuses = savedEntry.token === token ? (savedEntry.statuses || {}) : {};
      const initialStatuses = {};
      flowConfig.forEach(step => {
        initialStatuses[step] = savedStatuses[step] || 'pending';
      });

      const branchId = localStorage.getItem('temp_tokenBranchId');
      trackMobileCustomerFlowStarted({
        commerceId: branchId || 'mobile_flow',
        branchId: branchId || undefined,
        flowId: null
      });

      setFlowData({
        flow_id: null,
        tokenCustomerId,
        tokenRequestId,
        requestUid: requestUid || tokenRequestId,
        flow_config: flowConfig,
        flow_type: 'standard'
      });
      setStepStatuses(initialStatuses);
    } catch (err) {
      console.error('Error initializing flow:', err);
      setError(err.message || 'Este enlace ya no está disponible. Pedile a tu vendedor un nuevo enlace para continuar');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    initializeFlow();
  }, [initializeFlow]);

  /**
   * Actualiza el estado de un paso localmente y persiste en localStorage.
   * No llama a self-service/steps (ese endpoint requiere PurchaseRequestCustomerFlow).
   */
  const handleStepStatus = useCallback((stepData) => {
    const { step_name, step_status = 'completed' } = stepData;

    setStepStatuses(prev => {
      const updated = { ...prev, [step_name]: step_status };
      localStorage.setItem(STEP_STATUSES_KEY, JSON.stringify({ token, statuses: updated }));
      return updated;
    });
  }, [token]);

  const handleStepBack = () => {
    console.log('Step back requested');
  };

  if (loading) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Inicializando proceso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faExclamationCircle} size="3x" className="text-danger mb-3" />
          <h4>Atención</h4>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          <div className="card shadow-sm border-0 mt-3">
            <div className="card-body p-0">
              <CustomerFlow
                flowData={flowData}
                flowStatus="active"
                stepStatuses={stepStatuses}
                timeRemaining={0}
                onStepComplete={handleStepStatus}
                onStepBack={handleStepBack}
                searchParams={searchParams}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCustomerFlowPage;
